import NBType, { InferType } from '@netbase/nb-type'
import AuthorSchema from './AuthorSchema'
import ContentType from '../enums/ContentType'
import FirehoseStatus from '../enums/FirehoseStatus'
import Language from '../enums/Language'
import LogoTypeSchema from './LogoTypeSchema'
import Precision from '../enums/Precision'
import PremiumFeature from '../enums/PremiumFeature'
import ScopedStringListSchema from './ScopedStringListSchema'
import Source from '../enums/Source'
import TimeInterval from '../enums/TimeInterval'

/**
 * @typedef {import('./AuthorSchema').Author} Author
 * @typedef {import('../enums/ContentType').ContentType} ContentType
 * @typedef {import('../enums/FirehoseStatus').FirehoseStatus} FirehoseStatus
 * @typedef {import('../enums/Language').Language} Language
 * @typedef {import('./LogoTypeSchema').LogoType} LogoType
 * @typedef {import('../enums/Precision').Precision} Precision
 * @typedef {import('../enums/PremiumFeature').PremiumFeature} PremiumFeature
 * @typedef {import('./ScopedStringListSchema').ScopedStringList} ScopedStringList
 * @typedef {import('../enums/Source').Source} Source
 * @typedef {import('../enums/TimeInterval').TimeInterval} TimeInterval
 */

/**
 * @typedef {object} PreviewTopicSchema
 * @property {boolean} bigBang
 * @property {number} buildProgress
 * @property {ContentType} contentType
 * @property {LogoType[]} cvLogos
 * @property {Author[]} excludedAuthors
 * @property {string[]} excludedCountries
 * @property {string[]} excludedDomains
 * @property {string[][]} excludedMetaTags
 * @property {string[][]} excludedProductHierarchy
 * @property {ScopedStringList[]} excludedTerms
 * @property {string} facebookPage
 * @property {FirehoseStatus} firehoseStatus
 * @property {boolean} forCollectingAuthor
 * @property {number} fromDate
 * @property {string[]} hideInsights
 * @property {Author[]} includedAuthors
 * @property {string[]} includedCountries
 * @property {string[]} includedDomains
 * @property {string[][]} includedMetaTags
 * @property {string[][]} includedProductHierarchy
 * @property {ScopedStringList[]} includedTerms
 * @property {Language} language
 * @property {string[]} negativeInsights
 * @property {string[]} neutralInsights
 * @property {string[]} ocr
 * @property {string[]} positiveInsights
 * @property {PremiumFeature[]} premiumFeatures
 * @property {string[]} primaryTerms
 * @property {boolean} sampledGIIndex
 * @property {boolean} sampledIndex
 * @property {boolean} scale
 * @property {Precision} sentimentFocus
 * @property {Object[]} socialWebChannels
 * @property {Source[]} sources
 * @property {TimeInterval} timeInterval
 * @property {number} toDate
 * @property {number} topicId
 * @property {string} topicQuery
 * @property {string} twitterHandle
 */
const PreviewTopicSchema = NBType.object().shape('PreviewTopicSchema', {
  /* @prop */
  bigBang: NBType.boolean(),
  /* @prop */
  buildProgress: NBType.number(),
  /* @prop */
  contentType: NBType.enum(ContentType),
  /* @prop */
  cvLogos: NBType.array().of(LogoTypeSchema),
  /* @prop */
  excludedAuthors: NBType.array().of(AuthorSchema),
  /* @prop */
  excludedCountries: NBType.array().of(NBType.string()),
  /* @prop */
  excludedDomains: NBType.array().of(NBType.string()),
  /* @prop */
  excludedMetaTags: NBType.array().of(NBType.array().of(NBType.string())),
  /* @prop */
  excludedProductHierarchy: NBType.array().of(NBType.array().of(NBType.string())),
  /* @prop */
  excludedTerms: NBType.array().of(ScopedStringListSchema),
  /* @prop */
  facebookPage: NBType.string(),
  /* @prop */
  firehoseStatus: NBType.enum(FirehoseStatus),
  /* @prop */
  forCollectingAuthor: NBType.boolean(),
  /* @prop */
  fromDate: NBType.timestamp().nullable(),
  /* @prop */
  hideInsights: NBType.array().of(NBType.string()),
  /* @prop */
  includedAuthors: NBType.array().of(AuthorSchema),
  /* @prop */
  includedCountries: NBType.array().of(NBType.string()),
  /* @prop */
  includedDomains: NBType.array().of(NBType.string()),
  /* @prop */
  includedMetaTags: NBType.array().of(NBType.array().of(NBType.string())),
  /* @prop */
  includedProductHierarchy: NBType.array().of(NBType.array().of(NBType.string())),
  /* @prop */
  includedTerms: NBType.array().of(ScopedStringListSchema),
  /* @prop */
  language: NBType.enum(Language),
  /* @prop */
  negativeInsights: NBType.array().of(NBType.string()),
  /* @prop */
  neutralInsights: NBType.array().of(NBType.string()),
  /* @prop */
  ocr: NBType.array().of(NBType.string()),
  /* @prop */
  positiveInsights: NBType.array().of(NBType.string()),
  /* @prop */
  premiumFeatures: NBType.array().of(NBType.enum(PremiumFeature)),
  /* @prop */
  primaryTerms: NBType.array().of(NBType.string()),
  /* @prop */
  sampledGIIndex: NBType.boolean(),
  /* @prop */
  sampledIndex: NBType.boolean(),
  /* @prop */
  scale: NBType.boolean(),
  /* @prop */
  sentimentFocus: NBType.enum(Precision),
  /* @prop */
  socialWebChannels: NBType.freeObject(),
  /* @prop */
  sources: NBType.array().of(NBType.enum(Source)),
  /* @prop */
  timeInterval: NBType.enum(TimeInterval),
  /* @prop */
  toDate: NBType.timestamp().nullable(),
  /* @prop */
  topicId: NBType.number(),
  /* @prop */
  topicQuery: NBType.string(),
  /* @prop */
  twitterHandle: NBType.string()
  /* END */
})
export default PreviewTopicSchema
export type PreviewTopic = InferType<typeof PreviewTopicSchema>
// RESERVE BELOW
